/* eslint-disable no-unused-vars */
import { Reducer } from 'react';
import { ApolloError } from '@apollo/client';
import { LocaleServiceConfig } from '@volkswagen-onehub/locale-service';
import { Coordinates } from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import GoogleMapReact from 'google-map-react';
import { Dealer } from '../../models/dealer';
import { defaultState } from '../map-context';
import { DistanceUnit } from '../../models/common';
import { ctaBarButtonConfigProps } from '../../components/map/PartnerBusinessCard/pbc-types';
import {
  DealerReview,
  MyAudiBarButtonConfig,
  RatingSummary,
  ReviewCategory,
  ReviewSort,
  StarFilter,
} from '../../types';

export type State = {
  query: string;
  marketGoogleKey: string;
  devMode: boolean;
  pbcVersion: string;
  locale: Pick<LocaleServiceConfig, 'countryCode' | 'language'>;
  intlSearchEnabled: boolean;
  baseUrl: string;
  results: Dealer[] | null;
  dealerRatings: { [key: string]: number };
  dealerReviews: DealerReview[] | null;
  ratingSummary: RatingSummary | null;
  reviewCategoryFilter: ReviewCategory;
  reviewStarFilter: StarFilter;
  reviewSort: ReviewSort;
  isFlyoutOpen: boolean;
  hoveredDealer: Dealer | null;
  isGoogleMapsApiLoaded: boolean;
  currentResultsAvailableFilters: string[];
  authorDefinedFilters: string[];
  userSelectedFilters: string[];
  preselectedFilters: string[];
  isSidebarOpen: boolean;
  isAutocompletionOpen: boolean;
  isFiltersOverlayOpen: boolean;
  searchText: string;
  searchRadius: number;
  searchRadiusOptions: number[];
  initialMapZoomLevel: string;
  showAddress: boolean;
  isLoading: boolean;
  searchError: ApolloError | null;
  northEastPosition: Coordinates;
  southWestPosition: Coordinates;
  searchGeoLocation: Coordinates;
  placeId: string;
  isUserLocationError: boolean;
  isUserLocationPermissionGiven: boolean;
  isMapTriggeredByTeaser: boolean;
  showLocationError: boolean;
  updateMapZoomLevel: boolean;
  selectedDealerId: string | null;
  showRadius: boolean;
  searchRadiusExtended: boolean;
  isUserPositionUsed: boolean;
  groupMarkers: boolean;
  activeMobileView: 'map' | 'list';
  lastSubmittedSearch: null | string;
  mapZoom: number;
  searchInThisAreaVisible: boolean;
  bounds: GoogleMapReact.Bounds;
  mapCenterCoordinates: Coordinates;
  userGeolocation: Coordinates | null;
  distanceUnit: DistanceUnit;
  showPartnerSuggestions: boolean;
  ctaBarButtonConfig: ctaBarButtonConfigProps[];
  displayCtaBar: boolean;
  showOpeningHours: boolean;
  twoClickConsentRequired: boolean;
  twoClickData: {
    headline: string;
    copyText: string;
    toggleLabel: string;
    asideToggleHeadline: string;
    asideToggleLabel: string;
    asideToggleCopy: string;
    closeButton?: {
      text: string;
      redirectURL: string;
    };
  };
  fiveStarData: {
    displayFiveStarRating: boolean;
    legalText: string;
  };
  favoritePartner: {
    tenant: string;
    kvpsid: string;
  };
  myAudiCtaConfig: MyAudiBarButtonConfig | null;
  accessToken: string | null | undefined;
  selectedPartner: {
    tenant: string;
    kvpsid: string;
  };
  errorMessage: {
    code: string;
    message: string;
  };
  showModal: boolean;
};

// eslint-disable-next-line no-shadow
export enum ActionTypes {
  setQuery,
  setResults,
  setDealerRatings,
  setRatingSummary,
  setDealerReviews,
  setReviewCategoryFilter,
  setReviewStarFilter,
  setReviewSort,
  setHoveredDealer,
  setIsGoogleMapsApiLoaded,
  setIsAutocompletionOpen,
  setIsSidebarOpen,
  setIsFiltersOverlayOpen,
  setIsFlyoutOpen,
  setSearchText,
  setSearchRadius,
  addFilter,
  removeFilter,
  setFilters,
  setLoading,
  setSearchError,
  setSearchGeoLocation,
  setPlaceId,
  setNorthEastPosition,
  setSouthWestPosition,
  setUserLocationError,
  setUserGiveLocationPermission,
  setShowLocationError,
  resetGeolocationSearch,
  resetQuery,
  setUpdateMapZoomLevel,
  setSelectedDealerId,
  setShowRadius,
  setSearchRadiusExtended,
  setIsUserPositionUsed,
  setActiveMobileView,
  setLastSubmittedSearch,
  setMapZoom,
  setSearchInThisAreaVisible,
  setBounds,
  setMapCenterCoordinates,
  setUserGeoLocation,
  resetIsMapTriggeredByTeaser,
  setFavoritePartner,
  setMyAudiCtaConfig,
  setAccessToken,
  setSelectedPartner,
  setErrorMessage,
  setShowModal,
}

export type Action = {
  type: ActionTypes;
  // eslint-disable-next-line
  payload?: any;
};

const reducer: Reducer<State, Action> = (state: State, action: Action) => {
  const { type, payload } = action;

  switch (type) {
    case ActionTypes.setQuery:
      return { ...state, query: payload };
    case ActionTypes.setResults:
      return {
        ...state,
        results: payload.dealers,
        currentResultsAvailableFilters: payload.services,
      };
    case ActionTypes.setDealerRatings:
      return { ...state, dealerRatings: payload.dealerRatings };
    case ActionTypes.setDealerReviews:
      return { ...state, dealerReviews: payload };
    case ActionTypes.setReviewCategoryFilter:
      return { ...state, reviewCategoryFilter: payload };
    case ActionTypes.setReviewStarFilter:
      return { ...state, reviewStarFilter: payload };
    case ActionTypes.setReviewSort:
      return { ...state, reviewSort: payload };
    case ActionTypes.setRatingSummary:
      return { ...state, ratingSummary: payload };
    case ActionTypes.setIsFlyoutOpen:
      return { ...state, isFlyoutOpen: payload };
    case ActionTypes.setHoveredDealer:
      return { ...state, hoveredDealer: payload };
    case ActionTypes.addFilter:
      return { ...state, userSelectedFilters: [...state.userSelectedFilters, payload] };
    case ActionTypes.removeFilter: {
      const newFilters = state.userSelectedFilters;
      const index = state.userSelectedFilters.indexOf(payload);
      if (index >= 0) {
        newFilters.splice(index, 1);
      }
      return { ...state, userSelectedFilters: [...newFilters] };
    }
    case ActionTypes.setFilters:
      return { ...state, userSelectedFilters: [...payload] };
    case ActionTypes.setIsSidebarOpen:
      return { ...state, isSidebarOpen: payload };
    case ActionTypes.setSearchText:
      return { ...state, searchText: payload };
    case ActionTypes.setSearchRadius:
      return { ...state, searchRadius: payload };
    case ActionTypes.setIsGoogleMapsApiLoaded:
      return { ...state, isGoogleMapsApiLoaded: payload };
    case ActionTypes.setIsAutocompletionOpen:
      return { ...state, isAutocompletionOpen: payload };
    case ActionTypes.setSearchError:
      return { ...state, searchError: payload };
    case ActionTypes.setNorthEastPosition:
      return { ...state, northEastPosition: payload };
    case ActionTypes.setSouthWestPosition:
      return { ...state, southWestPosition: payload };
    case ActionTypes.setLoading:
      return { ...state, isLoading: payload };
    case ActionTypes.setSearchGeoLocation:
      return { ...state, searchGeoLocation: payload };
    case ActionTypes.setPlaceId:
      return { ...state, placeId: payload };
    case ActionTypes.setUserLocationError:
      return { ...state, isUserLocationError: payload };
    case ActionTypes.setUserGiveLocationPermission:
      return { ...state, isUserLocationPermissionGiven: payload };
    case ActionTypes.setShowLocationError:
      return { ...state, showLocationError: payload };
    case ActionTypes.resetGeolocationSearch:
      return { ...state, searchGeoLocation: defaultState.searchGeoLocation };
    case ActionTypes.resetQuery:
      return { ...state, query: defaultState.query };
    case ActionTypes.setUpdateMapZoomLevel:
      return { ...state, updateMapZoomLevel: payload };
    case ActionTypes.setSelectedDealerId:
      return { ...state, selectedDealerId: payload };
    case ActionTypes.setShowRadius:
      return { ...state, showRadius: payload };
    case ActionTypes.setSearchRadiusExtended:
      return { ...state, searchRadiusExtended: payload };
    case ActionTypes.setIsUserPositionUsed:
      return { ...state, isUserPositionUsed: payload };
    case ActionTypes.setLastSubmittedSearch:
      return { ...state, lastSubmittedSearch: payload };
    case ActionTypes.setMapZoom:
      return { ...state, mapZoom: payload };
    case ActionTypes.setSearchInThisAreaVisible:
      return { ...state, searchInThisAreaVisible: payload };
    case ActionTypes.setBounds:
      return { ...state, bounds: payload };
    case ActionTypes.setMapCenterCoordinates:
      return { ...state, mapCenterCoordinates: payload };
    case ActionTypes.setUserGeoLocation:
      return { ...state, userGeolocation: payload };
    case ActionTypes.setIsFiltersOverlayOpen:
      return { ...state, isFiltersOverlayOpen: payload };
    case ActionTypes.setActiveMobileView:
      return { ...state, activeMobileView: payload };
    case ActionTypes.resetIsMapTriggeredByTeaser:
      return { ...state, isMapTriggeredByTeaser: false };
    case ActionTypes.setFavoritePartner:
      return { ...state, favoritePartner: payload };
    case ActionTypes.setMyAudiCtaConfig:
      return { ...state, myAudiCtaConfig: payload };
    case ActionTypes.setAccessToken:
      return { ...state, accessToken: payload };
    case ActionTypes.setSelectedPartner:
      return { ...state, selectedPartner: payload };
    case ActionTypes.setErrorMessage:
      return { ...state, errorMessage: payload };
    case ActionTypes.setShowModal:
      return { ...state, showModal: payload };
    default:
      throw new Error(`Invalid action type: ${type}`);
  }
};

export default reducer;
